import { getContactByPhoneNumber } from 'services/pod';
import { attributionFromPhoneLookup } from 'components/CallScribe/phoneLookupAttribution';
import type { IInitiateCallRequest } from 'services/callScribe/types';
import { normalizeExternalTicketId } from 'components/CallScribe/ticketId';
import { formatPhoneE164ToDisplay } from 'components/CallScribe/Recents/recentsHelpers';

export const POPUP_LAUNCH_PARAM = 'launch';
const RESOLVE_CONTACT_PARAM = 'resolveContact';

export interface IPopupCallContext {
  toNumber: string;
  crmSourceId: number | null;
  externalTicketId: string | null;
  companyExternalId: string | null;
  contactExternalId: string | null;
  contactName: string | null;
  resolveContactByPhone: boolean;
}

type TContextParamField = Exclude<keyof IPopupCallContext, 'resolveContactByPhone'>;

const CONTEXT_PARAMS: Record<TContextParamField, string> = {
  toNumber: 'to',
  crmSourceId: 'toCrmSourceId',
  externalTicketId: 'toTicketId',
  companyExternalId: 'toCompanyId',
  contactExternalId: 'toContactId',
  contactName: 'toContactName',
};

export interface IOpenCallPopupParams {
  to: string;
  psaId: number | null;
  ticketId: string | null;
  contactExternalId?: string;
  companyExternalId?: string;
  contactName?: string;
  resolveContactByPhone?: boolean;
}

export const callContextFor = (params: IOpenCallPopupParams): IPopupCallContext => ({
  toNumber: params.to,
  crmSourceId: params.psaId ?? null,
  externalTicketId: normalizeExternalTicketId(params.ticketId),
  companyExternalId: params.companyExternalId ?? null,
  contactExternalId: params.contactExternalId ?? null,
  contactName: params.contactName ?? null,
  resolveContactByPhone: params.resolveContactByPhone ?? false,
});

export const newLaunchId = (): string =>
  Array.from(crypto.getRandomValues(new Uint8Array(8)), b => b.toString(16).padStart(2, '0')).join('');

export const writePopupCallContext = (
  url: URL,
  context: IPopupCallContext,
  launchId: string,
): void => {
  for (const [field, param] of Object.entries(CONTEXT_PARAMS) as Array<[TContextParamField, string]>) {
    const value = context[field];
    if (value === null) {
      url.searchParams.delete(param);
    } else {
      url.searchParams.set(param, String(value));
    }
  }
  if (context.resolveContactByPhone) {
    url.searchParams.set(RESOLVE_CONTACT_PARAM, '1');
  } else {
    url.searchParams.delete(RESOLVE_CONTACT_PARAM);
  }
  url.searchParams.set(POPUP_LAUNCH_PARAM, launchId);
};

const crmSourceFrom = (raw: string): number | null => {
  const value = Number(raw);
  return raw !== '' && Number.isFinite(value) ? value : null;
};

export const popupLaunchId = (search: string): string | null =>
  new URLSearchParams(search).get(POPUP_LAUNCH_PARAM);

export interface IUnlaunchedCallDefaults {
  crmSourceId: number | null;
  externalTicketId: string | null;
}

export const readPopupCallContext = (
  search: string,
  defaults: IUnlaunchedCallDefaults,
): IPopupCallContext => {
  const params = new URLSearchParams(search);
  const launched = params.has(POPUP_LAUNCH_PARAM);
  const read = (field: TContextParamField): string | null => params.get(CONTEXT_PARAMS[field]);
  const crmSource = read('crmSourceId');
  const ticket = read('externalTicketId');

  return {
    toNumber: read('toNumber') ?? '',
    crmSourceId: crmSource !== null
      ? crmSourceFrom(crmSource)
      : launched ? null : defaults.crmSourceId,
    externalTicketId: ticket !== null || launched ? ticket : defaults.externalTicketId,
    companyExternalId: read('companyExternalId'),
    contactExternalId: read('contactExternalId'),
    contactName: read('contactName'),
    resolveContactByPhone: params.get(RESOLVE_CONTACT_PARAM) === '1',
  };
};

export const resolveCallAttribution = async (
  context: IPopupCallContext,
): Promise<IPopupCallContext> => {
  if (!context.resolveContactByPhone || context.contactExternalId || !context.crmSourceId) {
    return context;
  }
  try {
    const res = await getContactByPhoneNumber({ crmId: context.crmSourceId, phone: context.toNumber });
    return { ...context, ...attributionFromPhoneLookup(res?.data) };
  } catch {
    return { ...context, contactExternalId: null, companyExternalId: null };
  }
};

export const initiateBodyFor = (context: IPopupCallContext): IInitiateCallRequest => ({
  fromNumber: '',
  toNumber: context.toNumber,
  crmSourceId: context.crmSourceId,
  externalTicketId: context.externalTicketId,
  companyExternalId: context.companyExternalId,
  contactExternalId: context.contactExternalId,
});

export const formattedNumber = (number: string): string => formatPhoneE164ToDisplay(number) || number;

export const calleeLabel = (context: IPopupCallContext): string =>
  context.contactName || formattedNumber(context.toNumber);
